import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import {
  classifyInstagramCallback,
  parseInstagramCallbackParams,
} from '../../lib/instagramConnect';
import { finalizeInstagramConnect } from './api';

// Per-account Instagram connect callback. Zernio redirects the browser here after
// the IG login (standard mode: ?connected=instagram&accountId=…&username=…; our
// kyck ref rides ?kyckAccountId=…). Unlike GBP there is NO picker — the IG
// SocialAccount is already created, so we just POST finalize (re-sync + capture the
// default) and confirm. See _architect/briefs/2026-07-16-instagram-connect-per-account.md.

export function InstagramCallbackPage() {
  const [params] = useSearchParams();
  const parsed = useMemo(() => parseInstagramCallbackParams(params), [params]);
  const outcome = classifyInstagramCallback(parsed);
  const accountId = parsed.kyckAccountId ?? '';

  const [done, setDone] = useState(false);
  const [handle, setHandle] = useState<string | null>(parsed.username ? `@${parsed.username.replace(/^@/, '')}` : null);

  const finalize = useMutation({
    mutationFn: () =>
      finalizeInstagramConnect({ accountId, username: parsed.username ?? undefined }),
    onSuccess: (res) => {
      setDone(true);
      if (res.instagram.username) setHandle(res.instagram.username);
    },
  });

  // Fire the finalize once on mount when we have a resolvable callback.
  const fired = useRef(false);
  useEffect(() => {
    if (outcome === 'ready' && !fired.current) {
      fired.current = true;
      finalize.mutate();
    }
  }, [outcome, finalize]);

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title="Connect Instagram"
        description="Linking the Instagram account to this account's profile."
        backHref={accountId ? `/accounts/${accountId}` : '/accounts'}
      />

      {outcome === 'error' ? (
        <p className="text-sm text-rose-600">
          Instagram couldn't be connected ({parsed.error}). Restart the connect flow from the
          account's API Connections section.
        </p>
      ) : outcome === 'missing-account' ? (
        <p className="text-sm text-slate-600">
          Missing the account reference. Restart the connect flow from the account's API
          Connections section.
        </p>
      ) : finalize.isError ? (
        <div className="space-y-3">
          <p className="text-sm text-rose-600">
            {(finalize.error as Error).message ||
              'Could not finalise the Instagram connection.'}
          </p>
          <Button size="sm" variant="outline" onClick={() => finalize.mutate()} disabled={finalize.isPending}>
            Try again
          </Button>
        </div>
      ) : done ? (
        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">Instagram connected</p>
            <p className="mt-0.5">
              {handle ? (
                <>
                  <span className="font-medium">{handle}</span> is now linked to this account's
                  profile.
                </>
              ) : (
                "Instagram is now linked to this account's profile."
              )}{' '}
              Comments, DMs and publishing to Instagram are now available.
            </p>
            <Button asChild size="sm" className="mt-3">
              <Link to={`/accounts/${accountId}`}>Back to the account</Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Finalising the Instagram connection…
        </div>
      )}
    </div>
  );
}
